// Recovery per body part (decision log 2026-10-15). Every session puts a load on six body parts (legs, back, chest,
// shoulders, arms, core): strength work by its hard sets (sets times intensity, a little more with a heavy weight),
// cardio by its minutes times effort (heart rate zones, else heart rate, felt effort or pace) and climbing, spread
// over the parts the sport uses. The load of a part decides how long it recovers: light about a day, moderate about
// two, heavy about three. Loads close together add up. While a part recovers, its exercises earn no XP (xp.ts);
// the session itself is never blocked. Pure functions, no clock. See docs/habit.md, "Recovery".
import { DEFAULT_MAX_HR, zoneOf, type CardioActivity } from '../cardio/activity';
import { activityKind } from '../cardio/sports';
import type { CardioSport, CardioTarget, Exercise, HeartRateZone, Workout } from '../types';
import { SECONDS_PER_REP } from '../workout';
import { addDays, localDay, localDayTime, weekStart, type Day } from './dates';
import { tzOffset } from './zoned';

export type BodyPart = 'legs' | 'back' | 'chest' | 'shoulders' | 'arms' | 'core';

export const BODY_PARTS: readonly BodyPart[] = ['legs', 'back', 'chest', 'shoulders', 'arms', 'core'];

export const BODY_PART_LABELS: Record<BodyPart, string> = {
  legs: 'Legs', back: 'Back', chest: 'Chest', shoulders: 'Shoulders', arms: 'Arms', core: 'Core',
};

/** The upper body parts, for suggestions ("Upper body or mobility today?"). */
const UPPER: readonly BodyPart[] = ['back', 'chest', 'shoulders', 'arms'];

export type PartWeights = Partial<Record<BodyPart, number>>;
export type PartLoad = Record<BodyPart, number>;

/** Which body parts a muscle word of the catalog (MUSCLES in catalog/define.ts) belongs to, and how much. */
export const MUSCLE_PARTS: Record<string, PartWeights> = {
  chest: { chest: 1 }, 'upper chest': { chest: 1 },
  shoulders: { shoulders: 1 }, 'front delts': { shoulders: 1 }, 'side delts': { shoulders: 1 }, 'rear delts': { shoulders: 0.7, back: 0.3 },
  triceps: { arms: 1 }, biceps: { arms: 1 }, forearms: { arms: 1 }, grip: { arms: 1 }, wrists: { arms: 0.5 },
  back: { back: 1 }, 'upper back': { back: 1 }, lats: { back: 1 }, traps: { back: 0.7, shoulders: 0.3 }, 'lower back': { back: 1 },
  neck: { back: 0.3 }, spine: { back: 0.5, core: 0.5 },
  core: { core: 1 }, abs: { core: 1 }, obliques: { core: 1 }, 'hip flexors': { core: 0.5, legs: 0.5 },
  glutes: { legs: 1 }, hips: { legs: 0.7, core: 0.3 }, adductors: { legs: 1 }, abductors: { legs: 1 }, quads: { legs: 1 },
  hamstrings: { legs: 1 }, calves: { legs: 1 }, shins: { legs: 1 }, ankles: { legs: 0.5 },
  'full body': { legs: 1, back: 0.7, core: 0.6, shoulders: 0.5, chest: 0.4, arms: 0.4 },
  // Not a body part: balance, heart and lungs.
};

/** The body parts each endurance sport works, the main one at 1. */
export const SPORT_PARTS: Record<CardioSport, PartWeights> = {
  running: { legs: 1, core: 0.15 },
  walking: { legs: 1 },
  hiking: { legs: 1, core: 0.1, back: 0.1 },
  // No impact on a bike or an elliptical: the legs carry less load than running for the same effort.
  cycling: { legs: 0.7, core: 0.1 },
  mountain_biking: { legs: 0.8, core: 0.2, arms: 0.25, shoulders: 0.15 },
  swimming: { shoulders: 1, back: 0.8, arms: 0.6, core: 0.4, chest: 0.3, legs: 0.3 },
  rowing: { back: 1, legs: 0.8, arms: 0.5, shoulders: 0.4, core: 0.3 },
  elliptical: { legs: 0.7, arms: 0.2 },
  skiing: { legs: 1, arms: 0.5, shoulders: 0.5, back: 0.5, core: 0.5 },
  inline_skating: { legs: 1, core: 0.3 },
  stair_climbing: { legs: 1 },
  jump_rope: { legs: 1, shoulders: 0.2, arms: 0.2 },
};

/** Ski touring climbs on the legs; the poles do less than in cross-country skiing. */
const SKI_TOURING: PartWeights = { legs: 1, core: 0.4, back: 0.4, shoulders: 0.3, arms: 0.3 };

export const RECOVERY_RULES = {
  /** Weight of the muscles in the order an exercise lists them (the first is the main one). */
  musclePosition: [1, 0.7, 0.5, 0.4, 0.3] as const,
  /** A hard set at intensity 1, 2, 3 (light, moderate, hard). */
  intensitySet: { 1: 0.5, 2: 0.8, 3: 1 } as const,
  /** Coordination work (ladders, balance, drills) loads this share of a strength set. */
  coordinationShare: 0.3,
  /** A full set has at least this many reps, or this many seconds for a timed set: less counts as part of a set. */
  fullSetReps: 3,
  /** A set at this share of your best weight or more is a full hard set whatever its reps. */
  heavyWeightShare: 0.85,
  fullSetSeconds: 40,
  /** Warm-up and cool-down work counts this share. */
  routineShare: 0.5,
  /** Long blocks without counted sets (non-stop, AMRAP) count at least one set per this much work (about 90 s). */
  workPerSet: 30,
  /** With a logged weight: the set counts from this share (light for you) to 1 + this (your heaviest), by your own best weight. */
  weightLow: 0.8,
  weightRange: 0.4,
  /**
   * Cardio: load per minute in zone 1 to 5. Steep on purpose: easy minutes count little (an hour in zone 2 is 2.4),
   * threshold and race pace a lot (40 minutes in zone 5 is a heavy day).
   */
  zoneLoadPerMinute: [0.01, 0.04, 0.14, 0.3, 0.5] as const,
  /** Climbing and descending: extra load per 100 m for foot sports and skiing, and for bikes (climbing only). */
  per100mFoot: 0.6,
  per100mBike: 0.3,
  /** Without heart rate or felt effort, the climb per hour sets the zone (bikes, hikes, skis): from these meters zone 3 and 4. */
  climbZone3PerHour: 400,
  climbZone4PerHour: 700,
  /** Loads per body part (hard sets) and the hours a part recovers, counted from the session's end, rounded up to the full hour. */
  levels: { light: 4, moderate: 9, heavy: 20 },
  hours: { light: 20, moderate: 44, heavy: 68 },
  /** The hard sets of a part in the sessions of this window before a session (end to start) add up with it. */
  togetherHours: 12,
} as const;

export type RecoveryLevel = 'none' | 'light' | 'moderate' | 'heavy';

/** What the recovery rules read of an exercise. */
export type RecoveryExercise = Pick<Exercise, 'muscles' | 'intensity'> & Partial<Pick<Exercise, 'activity' | 'sport' | 'category'>>;

/** What the recovery rules read of a block: the app's session log, the habit block and the coach's log all fit. */
export interface LoadBlock {
  sectionId?: string;
  exerciseId: string;
  done: { reps: number; seconds: number; times?: number };
  skipped: boolean;
  weightKg?: number | null;
  entries?: Array<{ reps: number; weightKg?: number | null; skipped: boolean; partial?: boolean; side?: 1 | 2 }>;
  /** Sets logged through the coach. */
  sets?: Array<{ reps?: number; seconds?: number; weightKg?: number }>;
}

export interface LoadSession {
  id: string;
  /** The library workout, for the cardio targets of its blocks (easy pace, zones). */
  workoutId?: string | null;
  startedAt: number;
  endedAt: number;
  activeSec: number;
  blocks?: LoadBlock[];
  /** The sections of a session run in the app: only blocks of main sections can lose XP. */
  sections?: Array<{ sectionId: string; kind: string }>;
  activity?: CardioActivity;
  /** How hard the whole session felt, 1 to 10 (coach logs). */
  rpe?: number;
}

export type ExerciseLookup = (id: string) => RecoveryExercise | undefined;

const r2 = (x: number) => Math.round(x * 100) / 100;
const emptyLoad = (): PartLoad => ({ legs: 0, back: 0, chest: 0, shoulders: 0, arms: 0, core: 0 });

function normalized(w: PartWeights): PartWeights {
  const max = Math.max(0, ...Object.values(w));
  if (max <= 0) return {};
  return Object.fromEntries(Object.entries(w).map(([p, v]) => [p, r2(v / max)]));
}

/** The body parts a sport works (ski touring has its own). */
export function sportParts(sport: CardioSport, variant?: string): PartWeights {
  return sport === 'skiing' && variant === 'touring' ? SKI_TOURING : SPORT_PARTS[sport];
}

/** The body parts an exercise works, the main one at 1. Cardio exercises use their sport. */
export function exerciseParts(e: RecoveryExercise): PartWeights {
  if (e.sport) return sportParts(e.sport.sport, e.sport.variant);
  const w: PartWeights = {};
  const mapped = e.muscles.map((m) => MUSCLE_PARTS[m.toLowerCase().trim()]).filter((x): x is PartWeights => !!x && Object.keys(x).length > 0);
  mapped.forEach((parts, i) => {
    const pos = RECOVERY_RULES.musclePosition[Math.min(i, RECOVERY_RULES.musclePosition.length - 1)]!;
    for (const [p, v] of Object.entries(parts) as Array<[BodyPart, number]>) w[p] = (w[p] ?? 0) + pos * v;
  });
  return normalized(w);
}

/** The body part an exercise works most; null for breathing and balance work. */
export function mainBodyPart(e: RecoveryExercise): BodyPart | null {
  const w = exerciseParts(e);
  let best: BodyPart | null = null;
  for (const p of BODY_PARTS) if ((w[p] ?? 0) > (best ? w[best] ?? 0 : 0)) best = p;
  return best;
}

export function loadLevel(load: number): RecoveryLevel {
  const L = RECOVERY_RULES.levels;
  if (load >= L.heavy) return 'heavy';
  if (load >= L.moderate) return 'moderate';
  if (load >= L.light) return 'light';
  return 'none';
}

export function recoveryHours(level: RecoveryLevel): number {
  return level === 'none' ? 0 : RECOVERY_RULES.hours[level];
}

// --- Cardio effort --------------------------------------------------------------------------------------------

const RPE_ZONE = (rpe: number): HeartRateZone => Math.min(5, Math.max(1, Math.ceil(Math.min(10, Math.max(1, rpe)) / 2))) as HeartRateZone;

/** A rough zone from speed, when nothing better is known (running only). */
function paceZone(a: Pick<CardioActivity, 'sport' | 'distanceM' | 'durationSec' | 'elevationGainM'>): HeartRateZone | null {
  if (!a.distanceM || a.durationSec <= 0) return null;
  const kmh = a.distanceM / 1000 / (a.durationSec / 3600);
  // Climbing makes the same speed harder: about 10 m up per km is worth 1 km/h on foot.
  const climb = a.elevationGainM ? a.elevationGainM / (a.distanceM / 1000) : 0;
  if (a.sport === 'running') {
    const v = kmh + climb / 10;
    return v < 9 ? 2 : v < 11 ? 3 : v < 13 ? 4 : 5;
  }
  return null;
}

/** A rough zone from the climb per hour, for bikes, hikes and skis without heart rate or felt effort (speed on a bike says little). */
function climbZone(a: Pick<CardioActivity, 'durationSec' | 'elevationGainM'>): HeartRateZone {
  const perHour = (a.elevationGainM ?? 0) / Math.max(1 / 60, a.durationSec / 3600);
  return perHour >= RECOVERY_RULES.climbZone4PerHour ? 4 : perHour >= RECOVERY_RULES.climbZone3PerHour ? 3 : 2;
}

/**
 * Load per minute of an activity: from the time in heart rate zones (when it covers at least half), else the average
 * heart rate, else how hard it felt, else the speed (running) or the climb per hour (bikes, hikes, skis). Also returns the zone used,
 * for the "light cardio" rule.
 */
export function cardioEffort(a: Pick<CardioActivity, 'sport' | 'durationSec' | 'zoneSec' | 'avgHr' | 'rpe' | 'distanceM' | 'elevationGainM'>, opts: { rpe?: number; maxHr?: number } = {}): { perMinute: number; zone: number } {
  const Z = RECOVERY_RULES.zoneLoadPerMinute;
  const total = a.zoneSec?.reduce((s, z) => s + z, 0) ?? 0;
  if (a.zoneSec && total > 0 && total >= a.durationSec * 0.5) {
    return {
      perMinute: a.zoneSec.reduce((s, z, i) => s + z * Z[i]!, 0) / total,
      zone: a.zoneSec.reduce((s, z, i) => s + z * (i + 1), 0) / total,
    };
  }
  const zone = a.avgHr !== undefined ? zoneOf(a.avgHr, opts.maxHr ?? DEFAULT_MAX_HR)
    : a.rpe !== undefined ? RPE_ZONE(a.rpe)
      : opts.rpe !== undefined ? RPE_ZONE(opts.rpe)
        : paceZone(a) ?? climbZone(a);
  return { perMinute: Z[zone - 1]!, zone };
}

/** Load of an activity on its main body part (before the sport's weights): minutes times effort, plus climbing. */
export function activityLoad(a: CardioActivity, opts: { rpe?: number; maxHr?: number } = {}): { load: number; light: boolean } {
  const minutes = Math.max(0, a.durationSec) / 60;
  const effort = cardioEffort(a, opts);
  const bike = a.sport === 'cycling' || a.sport === 'mountain_biking';
  const foot = ['running', 'walking', 'hiking', 'skiing', 'stair_climbing'].includes(a.sport);
  const climb = bike ? ((a.elevationGainM ?? 0) / 100) * RECOVERY_RULES.per100mBike
    : foot ? (((a.elevationGainM ?? 0) + (a.elevationLossM ?? 0)) / 100) * RECOVERY_RULES.per100mFoot : 0;
  const load = minutes * effort.perMinute + climb;
  return { load, light: effort.zone <= 2 };
}

/** The zone a workout's cardio block aims for, from its target (zone, effort or role), else from the exercise's intensity. */
function targetZone(target: CardioTarget | undefined, intensity: number): HeartRateZone {
  if (target?.zone) return target.zone;
  if (target?.rpe) return RPE_ZONE(target.rpe);
  if (target?.role === 'easy' || target?.role === 'recovery') return 2;
  return (intensity >= 3 ? 4 : intensity === 2 ? 3 : 2) as HeartRateZone;
}

function cardioTargetOf(workout: Pick<Workout, 'sections'> | undefined, b: LoadBlock): CardioTarget | undefined {
  if (!workout) return undefined;
  const sections = b.sectionId ? workout.sections.filter((s) => s.id === b.sectionId) : workout.sections;
  for (const s of sections) for (const x of s.blocks) if (x.exerciseId === b.exerciseId && x.cardio) return x.cardio;
  return undefined;
}

// --- Strength sets --------------------------------------------------------------------------------------------

/** The heaviest weight logged per exercise, for "a heavy set for you". */
export function bestWeights(sessions: readonly LoadSession[]): Map<string, number> {
  const best = new Map<string, number>();
  const note = (id: string, w: number | null | undefined) => {
    if (typeof w === 'number' && Number.isFinite(w) && w > 0) best.set(id, Math.max(best.get(id) ?? 0, w));
  };
  for (const s of sessions) for (const b of s.blocks ?? []) {
    note(b.exerciseId, b.weightKg);
    for (const e of b.entries ?? []) if (!e.skipped) note(b.exerciseId, e.weightKg);
    for (const e of b.sets ?? []) note(b.exerciseId, e.weightKg);
  }
  return best;
}

const work = (reps: number, seconds: number) => (reps > 0 ? reps : Math.max(0, seconds) / SECONDS_PER_REP);

/** Hard sets of a strength block, before intensity: counted sets (each scaled by its size and weight), at least its work over time. */
function strengthSets(b: LoadBlock, best: number | undefined): number {
  const R = RECOVERY_RULES;
  const weightFactor = (w: number | null | undefined) => (best && typeof w === 'number' && w > 0 ? R.weightLow + R.weightRange * Math.min(1, w / best) : 1);
  const heavy = (w: number | null | undefined) => !!best && typeof w === 'number' && w >= best * R.heavyWeightShare;
  const size = (reps: number, seconds: number, w?: number | null) => (reps > 0 && heavy(w) ? 1 : Math.min(1, reps > 0 ? reps / R.fullSetReps : Math.max(0, seconds) / R.fullSetSeconds));
  const total = work(b.done.reps, b.done.seconds);
  let sets = 0;
  const entries = (b.entries ?? []).filter((e) => !e.skipped && e.side !== 2);
  if (entries.length > 0) {
    const perEntrySeconds = b.done.reps > 0 ? 0 : b.done.seconds / entries.length;
    for (const e of entries) sets += (e.partial ? 0.5 : 1) * size(e.reps, perEntrySeconds, e.weightKg ?? b.weightKg) * weightFactor(e.weightKg ?? b.weightKg);
  } else if (b.sets && b.sets.length > 0) {
    for (const e of b.sets) sets += size(e.reps ?? 0, e.seconds ?? 0, e.weightKg) * weightFactor(e.weightKg);
  } else {
    const n = b.done.times && b.done.times > 0 ? b.done.times : total > 0 ? 1 : 0;
    sets = n * size(b.done.reps / Math.max(1, n), b.done.seconds / Math.max(1, n), b.weightKg) * weightFactor(b.weightKg);
  }
  return Math.max(sets, total / R.workPerSet);
}

// --- Load of a session ----------------------------------------------------------------------------------------

export interface LoadOptions {
  /** The heaviest weight per exercise (bestWeights over the history); without it, weights count as 1. */
  best?: Map<string, number>;
  /** The workout the session followed, for the cardio targets of its blocks. */
  workout?: Pick<Workout, 'sections'>;
  maxHr?: number;
}

const add = (into: PartLoad, parts: PartWeights, load: number) => {
  for (const p of BODY_PARTS) into[p] += (parts[p] ?? 0) * load;
};

/** The load of one block on its main body part, and whether it is light work that recovery never blocks. */
export function blockLoad(b: LoadBlock, e: RecoveryExercise, opts: LoadOptions = {}): { load: number; light: boolean } {
  if (b.skipped) return { load: 0, light: true };
  const kind = activityKind(e);
  if (kind === 'mobility') return { load: 0, light: true };
  if (kind === 'cardio') {
    const minutes = work(0, b.done.seconds > 0 ? b.done.seconds : b.done.reps * SECONDS_PER_REP) * SECONDS_PER_REP / 60;
    const zone = targetZone(cardioTargetOf(opts.workout, b), e.intensity);
    return { load: minutes * RECOVERY_RULES.zoneLoadPerMinute[zone - 1]!, light: zone <= 2 };
  }
  const sets = strengthSets(b, opts.best?.get(b.exerciseId)) * RECOVERY_RULES.intensitySet[e.intensity];
  if (kind === 'coordination') return { load: sets * RECOVERY_RULES.coordinationShare, light: true };
  return { load: sets, light: e.intensity <= 1 };
}

/**
 * The load a session puts on each body part. A session with an activity (a run, an import) counts the activity once,
 * not its stand-in block; strength blocks of the same session count on top.
 */
export function sessionLoad(s: LoadSession, exercise: ExerciseLookup, opts: LoadOptions = {}): PartLoad {
  const load = emptyLoad();
  const kindOf = new Map((s.sections ?? []).map((x) => [x.sectionId, x.kind]));
  for (const b of s.blocks ?? []) {
    const e = exercise(b.exerciseId);
    if (!e) continue;
    if (s.activity && activityKind(e) === 'cardio') continue;
    const kind = kindOf.get(b.sectionId ?? '') ?? (b.sectionId === 'warmup' || b.sectionId === 'cooldown' ? b.sectionId : 'main');
    add(load, exerciseParts(e), blockLoad(b, e, opts).load * (kind === 'main' ? 1 : RECOVERY_RULES.routineShare));
  }
  if (s.activity) add(load, sportParts(s.activity.sport, s.activity.variant), activityLoad(s.activity, { rpe: s.rpe, maxHr: opts.maxHr }).load);
  for (const p of BODY_PARTS) load[p] = r2(load[p]);
  return load;
}

// --- Recovery over time ---------------------------------------------------------------------------------------

export interface PartRecovery {
  part: BodyPart;
  /** The load still left now (each session's falls to 0 at its ready time). */
  load: number;
  /** The largest load still recovering (the hard sets of the window before it summed in). */
  peakLoad: number;
  level: RecoveryLevel;
  /** When the part is ready again (ms, a full hour); null when it is ready now. */
  readyAt: number | null;
  /** Until when its exercises earn no XP: the end of a moderate or heavy recovery; null when light or ready. */
  holdsXpUntil: number | null;
}

export type RecoveryState = Record<BodyPart, PartRecovery>;

interface Track { peak: number; from: number; readyAt: number }

const HOUR_MS = 3_600_000;
/**
 * Ready times are full hours on the person's clock ("Thu 15:00", not "Thu 14:36", also where the offset is not whole
 * hours, such as India): the rule and the words agree. Without a time zone, the runtime's own.
 */
function ceilHour(ms: number, timeZone?: string): number {
  const offset = timeZone ? tzOffset(ms, timeZone) : -new Date(ms).getTimezoneOffset() * 60_000;
  return Math.ceil((ms + offset) / HOUR_MS) * HOUR_MS - offset;
}

/** What is left of a load at a moment: it falls in a straight line to 0 at readyAt. */
const residual = (t: Track, at: number) => (at >= t.readyAt ? 0 : t.peak * Math.min(1, (t.readyAt - at) / Math.max(1, t.readyAt - t.from)));

/** At least moderate: only these hold back XP; a light load shows on Today only. */
const holdsXp = (level: RecoveryLevel) => level === 'moderate' || level === 'heavy';

export interface RecoveryInput {
  /** Every saved session; only those that started before `at` count. */
  sessions: readonly LoadSession[];
  exercise: ExerciseLookup;
  at: number;
  /** The workout of a session (usually by `workoutId`), for the cardio targets of its blocks. */
  workoutOf?: (s: LoadSession) => Pick<Workout, 'sections'> | undefined;
  /** The heaviest weight per exercise; by default from `sessions` (pass it when they are only the recent ones). */
  best?: Map<string, number>;
  /** The person's time zone: ready times are full hours there. */
  timeZone?: string;
  maxHr?: number;
}

/** A `workoutOf` from a lookup by workout id. */
export const workoutsById = (find: (id: string) => Pick<Workout, 'sections'> | undefined) => (s: LoadSession) => (s.workoutId ? find(s.workoutId) : undefined);

/**
 * The recovery of every body part at a moment. Sessions are read oldest first. For each part, a session's hard sets
 * add up with those of the sessions that ended within RECOVERY_RULES.togetherHours before it started (five short
 * sessions of 3 sets weigh like one of 15; a morning run adds to an evening leg session), and the sum's level sets
 * the recovery time from its end, rounded up to the full hour. A part is ready when every recovery time has passed.
 */
export function recoveryState(input: RecoveryInput): RecoveryState {
  const sessions = input.sessions.filter((s) => s.startedAt < input.at).sort((a, b) => a.startedAt - b.startedAt);
  const best = input.best ?? bestWeights(input.sessions);
  const tracks = new Map<BodyPart, Track[]>();
  const done: Array<{ end: number; load: PartLoad }> = [];
  const together = RECOVERY_RULES.togetherHours * HOUR_MS;
  for (const s of sessions) {
    const load = sessionLoad(s, input.exercise, { best, workout: input.workoutOf?.(s), maxHr: input.maxHr });
    const end = Math.max(s.endedAt, s.startedAt + Math.max(0, s.activeSec) * 1000);
    const window = done.filter((d) => s.startedAt - d.end < together);
    for (const p of BODY_PARTS) {
      if (load[p] <= 0) continue;
      const sum = window.reduce((x, d) => x + d.load[p], 0) + load[p];
      const level = loadLevel(sum);
      const hours = recoveryHours(level);
      if (hours === 0) continue;
      tracks.set(p, [...(tracks.get(p) ?? []), { peak: sum, from: end, readyAt: ceilHour(end + hours * HOUR_MS, input.timeZone) }]);
    }
    done.push({ end, load });
  }
  const out = {} as RecoveryState;
  for (const p of BODY_PARTS) {
    const open = (tracks.get(p) ?? []).filter((t) => input.at < t.readyAt);
    const readyAt = open.length ? Math.max(...open.map((t) => t.readyAt)) : null;
    const peak = open.length ? Math.max(...open.map((t) => t.peak)) : 0;
    const holding = open.filter((t) => holdsXp(loadLevel(t.peak)));
    out[p] = {
      part: p, load: r2(Math.max(0, ...open.map((t) => residual(t, input.at)))), peakLoad: r2(peak), level: readyAt === null ? 'none' : loadLevel(peak), readyAt,
      holdsXpUntil: holding.length ? Math.max(...holding.map((t) => t.readyAt)) : null,
    };
  }
  return out;
}

/** The parts still recovering (light ones too), soonest ready first. */
export function recoveringParts(state: RecoveryState): PartRecovery[] {
  return BODY_PARTS.map((p) => state[p]).filter((r) => r.readyAt !== null).sort((a, b) => a.readyAt! - b.readyAt!);
}

/** The parts whose exercises earn no XP now (moderate or heavy recovery), soonest ready first. */
export function holdingParts(state: RecoveryState): PartRecovery[] {
  return BODY_PARTS.map((p) => state[p]).filter((r) => r.holdsXpUntil !== null).sort((a, b) => a.holdsXpUntil! - b.holdsXpUntil!);
}

export interface RecoveryCheck {
  /** Exercises whose main part was still recovering at the session's start: they earn no XP. */
  blockedExercises: Set<string>;
  /** Per recovering part: its exercises in this session and when the part is ready. */
  blocked: Array<{ part: BodyPart; exerciseIds: string[]; readyAt: number }>;
  /** True when the session's activity (a run, a ride) is blocked as a whole. */
  activityBlocked: boolean;
}

/**
 * Which exercises of a session earn no XP because their main body part was in a moderate or heavy recovery when it
 * started. Never blocked: mobility, stretching and breathing, light exercises (intensity 1), coordination work and
 * easy cardio (zone 1 to 2, any length).
 */
export function recoveryCheck(input: {
  session: LoadSession; history: readonly LoadSession[]; exercise: ExerciseLookup; workout?: Pick<Workout, 'sections'>;
  workoutOf?: (s: LoadSession) => Pick<Workout, 'sections'> | undefined; maxHr?: number; timeZone?: string;
}): RecoveryCheck {
  const { session, exercise } = input;
  const state = recoveryState({ sessions: input.history.filter((s) => s.id !== session.id), exercise, at: session.startedAt, workoutOf: input.workoutOf, maxHr: input.maxHr, timeZone: input.timeZone });
  const best = bestWeights([...input.history, session]);
  const byPart = new Map<BodyPart, Set<string>>();
  const blockedExercises = new Set<string>();
  const mark = (part: BodyPart, id: string) => {
    blockedExercises.add(id);
    byPart.set(part, (byPart.get(part) ?? new Set()).add(id));
  };
  let activityBlocked = false;
  // Warm-up and cool-down never lose XP (they are not part of the main share anyway).
  const main = session.sections ? new Set(session.sections.filter((s) => s.kind === 'main').map((s) => s.sectionId)) : null;
  for (const b of session.blocks ?? []) {
    if (main ? !main.has(b.sectionId ?? '') : b.sectionId === 'warmup' || b.sectionId === 'cooldown') continue;
    const e = exercise(b.exerciseId);
    if (!e || (session.activity && activityKind(e) === 'cardio')) continue;
    const part = mainBodyPart(e);
    if (!part || state[part].holdsXpUntil === null) continue;
    const { load, light } = blockLoad(b, e, { best, workout: input.workout, maxHr: input.maxHr });
    if (!light && load > 0) mark(part, b.exerciseId);
  }
  if (session.activity) {
    const parts = sportParts(session.activity.sport, session.activity.variant);
    const part = BODY_PARTS.reduce<BodyPart | null>((m, p) => ((parts[p] ?? 0) > (m ? parts[m] ?? 0 : 0) ? p : m), null);
    const { light } = activityLoad(session.activity, { rpe: session.rpe, maxHr: input.maxHr });
    if (part && state[part].holdsXpUntil !== null && !light) {
      activityBlocked = true;
      byPart.set(part, byPart.get(part) ?? new Set());
    }
  }
  const blocked = [...byPart.entries()].map(([part, ids]) => ({ part, exerciseIds: [...ids], readyAt: state[part].holdsXpUntil! }));
  return { blockedExercises, blocked, activityBlocked };
}

/**
 * The parts a workout's main exercises would earn no XP for now, for the start screen's hint (and Today's card): the
 * same rule as recoveryCheck (light exercises, mobility, coordination and easy cardio never count).
 */
export function workoutRecovering(workout: Pick<Workout, 'sections'>, exercise: ExerciseLookup, state: RecoveryState): PartRecovery[] {
  const parts = new Set<BodyPart>();
  for (const s of workout.sections) {
    if (s.kind !== 'main') continue;
    for (const b of s.blocks) {
      const e = exercise(b.exerciseId);
      if (!e || e.intensity <= 1) continue;
      const kind = activityKind(e);
      if (kind === 'mobility' || kind === 'coordination') continue;
      if (kind === 'cardio' && targetZone(b.cardio, e.intensity) <= 2) continue;
      const p = mainBodyPart(e);
      if (p && state[p].holdsXpUntil !== null) parts.add(p);
    }
  }
  return holdingParts(state).filter((r) => parts.has(r.part));
}

// --- Words ----------------------------------------------------------------------------------------------------

const WEEKDAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const isoDow = (day: Day) => (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7;
const hhmm = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;

/** "today 18:00", "tomorrow 07:00" or "Thu 18:00", in the person's time zone. */
export function readyText(readyAt: number, now: number, timeZone?: string): string {
  const t = localDayTime(readyAt, timeZone);
  const today = localDay(now, timeZone);
  const when = t.day === today ? 'today' : t.day === addDays(today, 1) ? 'tomorrow' : WEEKDAY_ABBR[isoDow(t.day)]!;
  return `${when} ${hhmm(t.minute)}`;
}

/** Short, for the Today row: "until 18:00" today, else "until Thu". */
export function readyShortText(readyAt: number, now: number, timeZone?: string): string {
  const t = localDayTime(readyAt, timeZone);
  return t.day === localDay(now, timeZone) ? `until ${hhmm(t.minute)}` : `until ${WEEKDAY_ABBR[isoDow(t.day)]}`;
}

const listWords = (words: string[]) => (words.length <= 1 ? words.join('') : `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`);

/** What to train instead while parts recover: "Upper body or mobility today?"; null when everything is ready. */
export function recoverySuggestion(state: RecoveryState): string | null {
  // Only parts that hold back XP change what to train; a light load is fine to train again.
  const recovering = new Set(holdingParts(state).map((r) => r.part));
  if (recovering.size === 0) return null;
  const ready = BODY_PARTS.filter((p) => !recovering.has(p));
  if (ready.length === 0) return 'Mobility or an easy walk today?';
  const upperReady = UPPER.every((p) => !recovering.has(p));
  const words: string[] = [];
  if (upperReady) words.push('Upper body');
  if (!recovering.has('legs')) words.push('Legs');
  if (!upperReady) for (const p of UPPER) if (!recovering.has(p)) words.push(BODY_PART_LABELS[p]);
  if (!recovering.has('core') && !upperReady && recovering.has('legs')) words.push('Core');
  if (words.length === 0) words.push('Core');
  const [first, ...rest] = [...words.slice(0, 3), 'mobility'];
  return `${listWords([first!, ...rest.map((w) => (w === 'mobility' ? w : w.toLowerCase()))])} today?`;
}

/** "Legs recover until Thu 18:00." for one or more parts ("Legs and core recover until ..."). */
export function recoveringText(parts: PartRecovery[], now: number, timeZone?: string): string {
  if (parts.length === 0) return '';
  const last = parts.reduce((m, r) => Math.max(m, r.holdsXpUntil ?? r.readyAt ?? 0), 0);
  const names = parts.map((r, i) => (i === 0 ? BODY_PART_LABELS[r.part] : BODY_PART_LABELS[r.part].toLowerCase()));
  const who = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
  const plural = names.length > 1 || ['legs', 'shoulders', 'arms'].includes(parts[0]!.part);
  return `${who} ${plural ? 'recover' : 'recovers'} until ${readyText(last, now, timeZone)}.`;
}

// --- Weekly load, for Progress and the coach -------------------------------------------------------------------

/** The load per body part of each week (Monday first), oldest first, for `weeks` weeks up to the week of `today`. */
export function weeklyLoad(
  sessions: readonly LoadSession[], exercise: ExerciseLookup, today: Day, weeks: number, timeZone?: string, workoutOf?: (s: LoadSession) => Pick<Workout, 'sections'> | undefined,
): Array<{ weekOf: Day; load: PartLoad }> {
  const first = addDays(weekStart(today), -7 * (weeks - 1));
  const out = Array.from({ length: weeks }, (_, i) => ({ weekOf: addDays(first, 7 * i), load: emptyLoad() }));
  const best = bestWeights(sessions);
  for (const s of sessions) {
    const w = out.find((x) => x.weekOf === weekStart(localDay(s.startedAt, timeZone)));
    if (!w) continue;
    const load = sessionLoad(s, exercise, { best, workout: workoutOf?.(s) });
    for (const p of BODY_PARTS) w.load[p] = r2(w.load[p] + load[p]);
  }
  return out;
}
